<template>
	<div>
		<div class="d-flex flex-row align-center justify-space-between my-2 mb-0 px-4">
			<p class="mb-0 font-weight-bold body-2" style="color: #524D4D">Impuestos</p>
		</div>
		<v-divider class="my-2"></v-divider>
		
		<div class="px-4 mb-1">
			<v-alert
				dense
				text
				color="black"
				class="mb-2 body-2"
				style="font-size: 13px !important;"
			>
				<div class="d-flex align-start">
					<v-icon small color="warning" class="mr-2 mt-1">mdi-information-outline</v-icon>
					<div>
						<p class="mb-1 font-weight-medium">¿Cómo configurar tus impuestos?</p>
						<p class="mb-0" style="line-height: 1.4;">
							<strong>1. Activa</strong> los impuestos que tu empresa usará en Arquimarket<br>
							<strong>2. Selecciona</strong> cuál se aplicará automáticamente al crear nuevos elementos (como presupuestos o tablas de costo para los proyectos)
						</p>
					</div>
				</div>
			</v-alert>
		</div>

		<v-list class="pt-0 pb-0" style="max-height: 400px; overflow-y: auto;">
			<v-list-item
				v-for="(tax, i) in taxOptions"
				:key="tax.id || i"
				class="px-4 py-1"
				:class="{ 'tax-disabled': !tax.available }"
			>
				<v-list-item-content class="py-0">
					<div class="d-flex align-center justify-space-between">
						<!-- Tax info with checkbox -->
						<div class="d-flex align-center flex-grow-1">
							<v-checkbox
								:input-value="tax.available"
								dense
								hide-details
								color="black"
								class="ma-0 pa-0 mr-3 tax-custom-checkbox"
								@change="updateAvailability(tax)"
							>
								<template v-slot:label>
									<div class="d-flex align-center">
										<v-chip x-small outlined color="black" class="text-center d-flex justify-center" style="width: 50px;">{{ tax.value }}%</v-chip>
										<span class="caption ml-2">{{ tax.name }}</span>
									</div>
								</template>
							</v-checkbox>
						</div>

						<!-- Default radio button -->
						<div class="d-flex align-center">
							<v-tooltip bottom :disabled="tax.available">
								<template v-slot:activator="{ on, attrs }">
									<div v-bind="attrs" v-on="on">
										<v-radio-group
											:value="defaultTaxId"
											hide-details
											class="ma-0 pa-0"
											@change="setDefaultTax(tax)"
										>
											<v-radio
												:value="tax.system_tax_id"
												:disabled="!tax.available"
												color="black"
												class="ma-0 pa-0"
											>
												<template v-slot:label>
													<span class="caption grey--text">Predeterminado</span>
												</template>
											</v-radio>
										</v-radio-group>
									</div>
								</template>
								<span>Activa este impuesto primero</span>
							</v-tooltip>
						</div>
					</div>
				</v-list-item-content>
			</v-list-item>
		</v-list>

		<v-divider class="my-2"></v-divider>
		
		<div class="px-4 mb-2">
			<p class="caption mb-0" style="color: #999;">
				{{ selectedCount }} impuesto{{ selectedCount !== 1 ? 's' : '' }} seleccionado{{ selectedCount !== 1 ? 's' : '' }}
			</p>
		</div>
	</div>
</template>

<script>
import _debounce from 'lodash/debounce'
import { getTaxesOptions, setTaxOption } from '@/helpers/api/general'

export default {
  name: 'CompanyTaxesConfig',

  data: () => ({
    systemTaxes: [],
		pendingTaxPayloads: {},
		debouncedSavers: {}
  }),

  computed: {
    selectedCompany() {
      return this.$store.state.companies.selectedCompany
    },

    taxOptions() {
      return this.systemTaxes.map(tax => ({
        system_tax_id: tax.id,
        name: tax.name,
        value: tax.value,
        available: !!tax.company_tax,
        default: !!tax.company_tax?.default
      }))
    },

		defaultTaxId() {
			const defaultTax = this.taxOptions.find(t => t.default && t.available)
			return defaultTax ? defaultTax.system_tax_id : null
		},

		selectedCount() {
			return this.taxOptions.filter(t => t.available).length
		}
  },

  mounted() {
    this.getTaxes()
  },

  methods: {
    async getTaxes() {
      const res = await getTaxesOptions()
      if (res.code === 200) {
        this.systemTaxes = res.taxes
      }
    },

		getDebouncedSaver(systemTaxId) {
			if (!this.debouncedSavers[systemTaxId]) {
				this.debouncedSavers[systemTaxId] = _debounce(
					() => this.flushSave(systemTaxId),
					400
				)
			}
			return this.debouncedSavers[systemTaxId]
		},

    updateAvailability(tax) {
			const id = tax.system_tax_id
			const newAvailable = !tax.available

			// Actualización optimista: cambiar el estado inmediatamente en el frontend
			this.systemTaxes = this.systemTaxes.map(t => {
				if (t.id === id) {
					return {
						...t,
						company_tax: newAvailable ? {
							...t.company_tax,
							default: t.company_tax?.default || false
						} : null
					}
				}
				return t
			})

			// Si se está desactivando y era el por defecto, hay que quitarle el default
			this.pendingTaxPayloads[id] = {
				system_tax_id: id,
				available: newAvailable,
				default: newAvailable && tax.default // Mantiene default solo si sigue disponible
			}

			this.getDebouncedSaver(id)()
		},

		setDefaultTax(tax) {
			if (!tax.available) return

			const id = tax.system_tax_id

			// Actualización optimista: cambiar el estado inmediatamente en el frontend
			this.systemTaxes = this.systemTaxes.map(t => {
				if (t.id === id) {
					// Activar el nuevo default
					return {
						...t,
						company_tax: {
							...t.company_tax,
							default: true
						}
					}
				} else if (t.company_tax?.default) {
					// Desactivar el anterior default
					return {
						...t,
						company_tax: {
							...t.company_tax,
							default: false
						}
					}
				}
				return t
			})

			// Primero, quitar el default de todos los demás
			this.taxOptions.forEach(t => {
				if (t.system_tax_id !== id && t.default) {
					this.pendingTaxPayloads[t.system_tax_id] = {
						system_tax_id: t.system_tax_id,
						available: t.available,
						default: false
					}
					this.getDebouncedSaver(t.system_tax_id)()
				}
			})

			// Luego, establecer el nuevo default
			this.pendingTaxPayloads[id] = {
				system_tax_id: id,
				available: true,
				default: true
			}

			this.getDebouncedSaver(id)()
		},

		async flushSave(systemTaxId) {
			const payload = this.pendingTaxPayloads[systemTaxId]
			if (!payload) return

			delete this.pendingTaxPayloads[systemTaxId]

			await setTaxOption({
				company_id: this.selectedCompany.id,
				system_tax_id: payload.system_tax_id,
				available: payload.available,
				default: payload.default
			})

			await this.getTaxes()
		}
  }
}
</script>

<style scoped>
/deep/ .tax-custom-checkbox .v-input--selection-controls__input {
	margin: 0;
}

/deep/ .tax-custom-checkbox .v-label {
	margin-bottom: 0;
}

.tax-disabled {
	opacity: 0.5;
}

/* Smooth transitions */
.v-list-item {
	transition: background-color 0.2s ease;
}

.v-list-item:hover {
	background-color: rgba(0, 0, 0, 0.02);
}
</style>